import { inject } from '@angular/core';
import { Injectable } from '@banta/common';
import { ApiNotFoundError, Requester, RequestPerformer } from '@tytapp/api/requester';
import { buildQuery, LoggerService } from '@tytapp/common';
import { catchError, Observable, of, throwError } from 'rxjs';

export interface ApiRequest<T = any> {
    method: string;
    path: string;
    query: Record<string, string>;
    headers: Record<string, string|string[]>;
    body: T;
}

export type ApiPerformer<ResponseT = any> = (request: ApiRequest) => Observable<ResponseT>;
export type ApiMiddleware<ResponseT = any> = (request: ApiRequest, next: ApiPerformer<ResponseT>) => Observable<ResponseT>;

@Injectable()
export class ApiMiddlewareService {
    private apiRequester = inject(Requester);
    private logger = inject(LoggerService).configure({ source: 'apimiddle' });
    private preRequestPerformer: RequestPerformer = this.apiRequester.requestPerformer;

    constructor() {
        // Set up the ability to have pre-middleware.
        this.install('pre', (request, next) => {
            return this.preRequestPerformer(request.method, request.path, request.query, request.headers, request.body);
        });

        // The whole codebase expects undefined when a 404 occurs, not an exception
        this.install('404-as-undefined', (request, next) => {
            return next(request)
                .pipe(catchError(e => {
                    if (e instanceof ApiNotFoundError)
                        return of(undefined);
                    return throwError(() => e);
                }))
            ;
        })
    }

    /**
     * Add a new pre-middleware. Pre-middlewares are applied before any normal middleware.
     * This is useful for things like simulations (see live stream simulations)
     * @param name
     * @param middleware
     */
    installPre(name: string, middleware: ApiMiddleware) {
        let inner = this.preRequestPerformer;
        let callMiddleware: RequestPerformer = (method, path, query, headers, body) => {

            //this.logger.info(`${method} ${path}?${buildQuery(query)}: Running middleware ${name}`);

            return middleware(
                { method, path, query, headers, body },
                (request) => {
                    //this.logger.info(`${method} ${path}?${buildQuery(query)}: Finished middleware ${name}`);
                    return inner(request.method, request.path, request.query, request.headers, request.body)
                }
            );
        }

        this.preRequestPerformer = callMiddleware;
    }

    /**
     * Install the given middleware into the API request stack.
     * @param middleware
     */
    install(name: string, middleware: ApiMiddleware) {
        let inner = this.apiRequester.requestPerformer;
        let callMiddleware: RequestPerformer = (method, path, query, headers, body) => {

            //this.logger.info(`${method} ${path}?${buildQuery(query)}: Running middleware ${name}`);

            return middleware(
                { method, path, query, headers, body },
                (request) => {
                    //this.logger.info(`${method} ${path}?${buildQuery(query)}: Finished middleware ${name}`);
                    return inner(request.method, request.path, request.query, request.headers, request.body)
                }
            );
        }

        this.apiRequester.requestPerformer = callMiddleware;
    }
}